export const RAID_CONTROL_ACTION_TYPES = Object.freeze(['self-request', 'order']);

export function activateMobileChatComposerWithoutScroll(event, activeElement, textarea) {
  if (!textarea || typeof textarea.focus !== 'function') return false;
  if (event?.cancelable && typeof event.preventDefault === 'function') event.preventDefault();
  if (activeElement !== textarea) textarea.focus({ preventScroll: true });
  return true;
}

export function reduceRaidControlActionState(current, event) {
  if (!event || event.type === 'close') return null;
  if (event.type !== 'open' || !RAID_CONTROL_ACTION_TYPES.includes(event.action)) {
    throw new Error('Unknown raid control action transition.');
  }
  return event.action;
}

export function invokeMobileChatPlusAction(action, handlers) {
  if (action === 'self-request') handlers.onSelfRequestOpen();
  else if (action === 'order') handlers.onOrderOpen();
  else throw new Error('Unknown mobile chat plus action.');
  return action;
}

export function targetPlusMenuSettleVersion(settleVersion, requiresCloseCycle) {
  return Number(settleVersion || 0) + (requiresCloseCycle ? 1 : 0);
}

export function canOpenPlusMenu({ viewportSettled, settleVersion, targetSettleVersion }) {
  return Boolean(viewportSettled) && Number(settleVersion || 0) >= Number(targetSettleVersion || 0);
}

export function isMobileChatKeyboardVisuallyOpen({ height, closedHeight }) {
  const currentHeight = Math.max(1, Math.round(Number(height || 0)));
  const baseline = Math.max(0, Math.round(Number(closedHeight || 0)));
  return baseline > 0 && baseline - currentHeight >= 80;
}

export function createMobileChatKeyboardGate() {
  return {
    closedHeight: 0,
    keyboardLatched: false,
    settled: false,
    stableFrames: 0,
    candidateHeight: 0,
    phase: 'closed',
    needsFrameSample: false,
  };
}

const closeTolerance = (baseline) => Math.max(2, Math.round(Number(baseline || 0) * 0.005));

export function nextMobileChatKeyboardGate(current, sample) {
  const height = Math.max(1, Math.round(Number(sample?.height || 0)));
  const composerFocused = Boolean(sample?.composerFocused);
  const state = current || createMobileChatKeyboardGate();

  if (!state.closedHeight) {
    if (composerFocused) {
      return { ...state, closedHeight: height, keyboardLatched: true, settled: false, phase: 'opening' };
    }
    return {
      closedHeight: height,
      keyboardLatched: false,
      settled: true,
      stableFrames: 2,
      candidateHeight: 0,
      phase: 'closed',
      needsFrameSample: false,
    };
  }

  const baseline = Number(state.closedHeight);
  const tolerance = closeTolerance(baseline);
  const nearClosedBaseline = Math.abs(baseline - height) <= tolerance;
  const viewportShrunk = isMobileChatKeyboardVisuallyOpen({ height, closedHeight: baseline });

  if (composerFocused || viewportShrunk) {
    return {
      ...state,
      keyboardLatched: true,
      settled: false,
      stableFrames: 0,
      candidateHeight: 0,
      phase: viewportShrunk ? 'open' : 'opening',
      needsFrameSample: false,
    };
  }

  if (state.keyboardLatched) {
    const stableFrames = nearClosedBaseline ? Number(state.stableFrames || 0) + 1 : 0;
    if (nearClosedBaseline && stableFrames >= 2) {
      return {
        closedHeight: height,
        keyboardLatched: false,
        settled: true,
        stableFrames: 2,
        candidateHeight: 0,
        phase: 'closed',
        needsFrameSample: false,
      };
    }
    return {
      ...state,
      settled: false,
      stableFrames,
      candidateHeight: 0,
      phase: 'closing',
      needsFrameSample: nearClosedBaseline,
    };
  }

  if (nearClosedBaseline) {
    return {
      ...state,
      closedHeight: height,
      settled: true,
      stableFrames: 2,
      candidateHeight: 0,
      phase: 'closed',
      needsFrameSample: false,
    };
  }

  const candidateHeight = Number(state.candidateHeight || height);
  const candidateStable = Math.abs(candidateHeight - height) <= closeTolerance(candidateHeight);
  const stableFrames = candidateStable ? Number(state.stableFrames || 0) + 1 : 1;
  if (candidateStable && stableFrames >= 2) {
    return {
      closedHeight: height,
      keyboardLatched: false,
      settled: true,
      stableFrames: 2,
      candidateHeight: 0,
      phase: 'closed',
      needsFrameSample: false,
    };
  }
  return {
    ...state,
    settled: false,
    stableFrames,
    candidateHeight: height,
    phase: 'settling',
    needsFrameSample: true,
  };
}
